type RequestHeaders =
  | Record<string, unknown>
  | {
      get?: (name: string) => unknown;
    }
  | undefined;

const LOGIN_ENDPOINTS = new Set([
  '/api/auth/login',
  '/api/mi-mobile/auth/login',
  '/api/franchisee-accounts/login',
  '/api/fgshop/auth/login',
]);

const getPathname = (url: string): string => {
  try {
    return new URL(url, 'https://internal.flashgreen.local').pathname.toLowerCase();
  } catch {
    return url.split('?')[0].toLowerCase();
  }
};

export const isAuthenticationLoginRequest = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return LOGIN_ENDPOINTS.has(getPathname(url));
};

export const getBearerTokenFromHeaders = (headers: RequestHeaders): string | null => {
  if (!headers) {
    return null;
  }

  const getter = (headers as { get?: (name: string) => unknown }).get;
  const rawHeader =
    (typeof getter === 'function' ? getter.call(headers, 'Authorization') : undefined) ??
    (headers as Record<string, unknown>).Authorization ??
    (headers as Record<string, unknown>).authorization;

  if (typeof rawHeader !== 'string') {
    return null;
  }

  const match = rawHeader.match(/^Bearer\s+(.+)$/i);
  return match?.[1]?.trim() || null;
};

/**
 * A delayed 401 from the token that was replaced by a successful login must
 * not clear the newly-issued session. Login endpoint failures are credential
 * errors and likewise must not be treated as an expired active session.
 */
export const shouldIgnoreAdminSession401 = ({
  requestUrl,
  requestHeaders,
  currentToken,
}: {
  requestUrl?: string;
  requestHeaders?: RequestHeaders;
  currentToken: string | null;
}): boolean => {
  if (isAuthenticationLoginRequest(requestUrl)) {
    return true;
  }

  const requestToken = getBearerTokenFromHeaders(requestHeaders);
  return Boolean(requestToken && currentToken && requestToken !== currentToken);
};
